import { useState } from 'react';
import { db } from '@/lib/db';
import type { Member } from '@/lib/db/types';
import { uploadFile } from '@/lib/storage';
import { Sheet } from '@/components/shared/Sheet';
import { ImageCropper } from '@/components/shared/ImageCropper';
import { StorageImage } from '@/components/shared/StorageImage';

interface Props {
  member: Member;
  onClose: () => void;
  onSaved: (m: Member) => void;
}

export function ProfilePhotoModal({ member, onClose, onSaved }: Props) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(member.profile_photo_url);
  const [uploading, setUploading] = useState(false);

  const save = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const url = await uploadFile('profile-photos', blob, 'profile.jpg');
      onSaved(await db.members.update({ profile_photo_url: url }));
      onClose();
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    setUploading(true);
    try {
      onSaved(await db.members.update({ profile_photo_url: null }));
      onClose();
    } finally {
      setUploading(false);
    }
  };

  return (
    <Sheet title="Profile Photo" onClose={onClose}>
      <div className="space-y-4 p-4">
        {preview && <StorageImage src={preview} alt="" className="mx-auto aspect-square w-48 rounded-2xl object-cover shadow-md" />}
        <ImageCropper label="Upload new photo" onCropped={(b, url) => { setBlob(b); setPreview(url); }} />
        <button type="button" disabled={!blob || uploading} onClick={() => void save()} className={`w-full rounded-full py-3 text-[13px] font-bold ${blob ? 'bg-navy text-paper' : 'bg-line text-mute opacity-50'}`}>{uploading ? 'Uploading…' : 'Save Photo'}</button>
        {member.profile_photo_url && (
          <button type="button" disabled={uploading} onClick={() => void remove()} className="w-full rounded-full border border-line bg-cream py-3 text-[13px] font-bold text-bad">Remove Photo</button>
        )}
      </div>
    </Sheet>
  );
}
